import type {
  AgentDetail,
  AgentReport,
  AuditEntry,
  GovernanceStatus,
} from "../../api/admin-agents";
import { Td, Th, fmtDate, shortId } from "../../components/DataTable";
import { t } from "../../i18n";
import { s } from "./agent-detail-styles";

const TABS = ["Overview", "Governance History", "Reports"] as const;
export type AgentDetailTab = (typeof TABS)[number];

const TAB_KEYS: Record<AgentDetailTab, string> = {
  Overview: "agent_detail.tab_overview",
  "Governance History": "agent_detail.tab_governance_history",
  Reports: "agent_detail.tab_reports",
};

export function InfoCard({
  agent,
}: {
  agent: AgentDetail;
}): React.JSX.Element {
  const mono = { ...s.infoValue, fontFamily: "var(--font-mono)", fontSize: 12 };
  return (
    <div style={s.card}>
      <InfoRow
        label={t("agent_detail.agent_id")}
        value={<span style={mono}>{agent.id}</span>}
      />
      <InfoRow
        label={t("agent_detail.status")}
        value={<StatusBadge status={agent.governance_status} />}
      />
      <InfoRow
        label={t("agent_detail.listing_status")}
        value={`${agent.listing.visibility} / ${agent.listing.listing_status}`}
      />
      <InfoRow
        label={t("agent_detail.monetization_status")}
        value={agent.listing.monetization_status}
      />
      <InfoRow
        label={t("agent_detail.connection")}
        value={agent.connection_mode}
      />
      <InfoRow
        label={t("agent_detail.risk_score")}
        value={String(agent.risk_score)}
      />
      <InfoRow
        label={t("agent_detail.reports")}
        value={String(agent.report_count)}
      />
      <InfoRow
        label={t("agent_detail.description")}
        value={agent.description ?? "-"}
      />
      <InfoRow
        label={t("agent_detail.created")}
        value={fmtDate(agent.created_at)}
      />
    </div>
  );
}

export function H2aStatsBar({
  stats,
}: {
  stats: AgentDetail["h2a_stats"];
}): React.JSX.Element {
  return (
    <div style={s.statsGrid}>
      <StatCard
        label={t("agent_detail.h2a_active")}
        value={stats.active}
        color="#166534"
      />
      <StatCard
        label={t("agent_detail.h2a_suspended")}
        value={stats.suspended}
        color="#991b1b"
      />
      <StatCard
        label={t("agent_detail.h2a_terminated")}
        value={stats.terminated}
        color="#6b7280"
      />
    </div>
  );
}

export function AgentDetailTabBar({
  active,
  onChange,
}: {
  active: AgentDetailTab;
  onChange: (tab: AgentDetailTab) => void;
}): React.JSX.Element {
  return (
    <div style={s.tabBar}>
      {TABS.map((tab) => (
        <button
          key={tab}
          type="button"
          onClick={() => onChange(tab)}
          style={tab === active ? { ...s.tab, ...s.tabActive } : s.tab}
        >
          {t(TAB_KEYS[tab])}
        </button>
      ))}
    </div>
  );
}

export function AgentDetailTabContent({
  tab,
  agent,
}: {
  tab: AgentDetailTab;
  agent: AgentDetail;
}): React.JSX.Element {
  if (tab === "Governance History") {
    return <AuditTimeline records={agent.audit_records} />;
  }
  if (tab === "Reports") {
    return <ReportsTable reports={agent.reports} />;
  }
  return <OverviewTab agent={agent} />;
}

function InfoRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): React.JSX.Element {
  return (
    <div style={s.infoRow}>
      <span style={s.infoLabel}>{label}</span>
      <span style={s.infoValue}>{value}</span>
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}): React.JSX.Element {
  return (
    <div style={s.statCard}>
      <span style={s.statLabel}>{label}</span>
      <span style={{ ...s.statValue, color }}>{value}</span>
    </div>
  );
}

function OverviewTab({ agent }: { agent: AgentDetail }): React.JSX.Element {
  return (
    <div style={s.statsGrid}>
      {overviewRows(agent).map(([label, value]) => (
        <div key={label} style={s.statCard}>
          <span style={s.statLabel}>{label}</span>
          <span style={s.statValue}>{value}</span>
        </div>
      ))}
    </div>
  );
}

function AuditTimeline({
  records,
}: {
  records: ReadonlyArray<AuditEntry>;
}): React.JSX.Element {
  if (records.length === 0) {
    return <p style={s.emptyTab}>{t("agent_detail.no_governance_history")}</p>;
  }
  return (
    <div style={s.timeline}>
      {records.map((record) => (
        <div key={record.id} style={s.timelineItem}>
          <div style={s.timelineDot} />
          <div style={s.timelineContent}>
            <span style={s.timelineAction}>{record.action}</span>
            <span style={s.timelineMeta}>
              {shortId(record.actor_id)} &middot; {fmtDate(record.created_at)}
            </span>
            {record.reason && <p style={s.timelineReason}>{record.reason}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReportsTable({
  reports,
}: {
  reports: ReadonlyArray<AgentReport>;
}): React.JSX.Element {
  if (reports.length === 0) {
    return <p style={s.emptyTab}>{t("agent_detail.no_reports")}</p>;
  }
  return (
    <table style={s.table}>
      <thead>
        <tr>
          <Th>{t("agent_detail.col_reporter")}</Th>
          <Th>{t("agent_detail.col_reason")}</Th>
          <Th>{t("agent_detail.col_status")}</Th>
          <Th>{t("agent_detail.col_date")}</Th>
        </tr>
      </thead>
      <tbody>
        {reports.map((report) => (
          <tr key={report.id}>
            <Td mono>{shortId(report.reporter_id)}</Td>
            <Td>{truncate(report.reason, 80)}</Td>
            <Td>{report.status}</Td>
            <Td>{fmtDate(report.created_at)}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatusBadge({
  status,
}: {
  status: GovernanceStatus;
}): React.JSX.Element {
  const color = GOVERNANCE_COLOR[status];
  return (
    <span style={{ ...s.badge, background: color.bg, color: color.fg }}>
      {capitalize(status)}
    </span>
  );
}

function overviewRows(
  agent: AgentDetail,
): ReadonlyArray<readonly [string, string]> {
  return [
    [t("agent_detail.connection_mode"), agent.connection_mode],
    [t("agent_detail.risk_score"), String(agent.risk_score)],
    [t("agent_detail.report_count"), String(agent.report_count)],
    [
      t("agent_detail.listing_status"),
      `${agent.listing.visibility} / ${agent.listing.listing_status}`,
    ],
    [t("agent_detail.monetization_status"), agent.listing.monetization_status],
    [t("agent_detail.last_updated"), fmtDate(agent.updated_at)],
  ];
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}...` : value;
}

const GOVERNANCE_COLOR: Record<GovernanceStatus, { bg: string; fg: string }> = {
  active: { bg: "#dcfce7", fg: "#166534" },
  restricted: { bg: "#fef9c3", fg: "#854d0e" },
  suspended: { bg: "#fef2f2", fg: "#991b1b" },
  banned: { bg: "#f3f4f6", fg: "#6b7280" },
};
